import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

/**
 * Translate key to localized text.
 * Sample:
 * <Translate ns='prelogin_pages'>{
        t => <div alt={t('fitness_at_your_fingers_content')} />
    }</Translate>
    if multiple ns are provided, t function requires ns:key format
 * <Translate ns={['prelogin_pages', 'device']}>{
        t => <img src={iphoneMobileMyDay} className={classes.iphoneMobileApp}
                  title={t('device:device.support')}
                  alt={t('prelogin_pages:fitness_at_your_fingers_content')} />
    }</Translate>
 *  <Translate ns="activity">key</Translate>
 *  <Translate ns="activity" content="key"/>
 *  <Translate ns="activity" content="key_with_place_holder" params={{0: 'param1', 1: 'param2'}}/>
 *  <Translate ns="activity" content="key_with_place_holder" params={{0: <div>html element</div>}}/>
 *
 * To use pre-existing translation keys that already have HTML elements inside of them, provide a 'tag'
 *  <Translate ns='activity' content='key_with_html_inside_it' tag='span'/>
 *
 * If your text has encoded HTML tag, you'll need to pass a wrapper tag as below
 *  <Translate tag="div" className="" ns="activity" content="key"/>
 */
const Translate = ({ ns, content, params, tag, className, attr, prepend, children, t, i18n }) => {
    const isMounted = useRef(false);
    const [text, setText] = useState('');
    const [parts, setParts] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const translate = (key) => {
        if (!loaded) {
            return '';
        }

        if (Array.isArray(ns)) {
            // if ns is array, the key needs to be in ns:key format, no need to add ns
            return t(`${key}`, params);
        } else {
            return t(`${ns}:${key}`, params);
        }
    };

    const loadText = useCallback(() => {
        let key = children || content;

        if (!key) {
            throw Error('children or content must be provided');
        }

        i18n.loadNamespaces(ns, () => {
            let text = t(`${ns}:${key}`, { skipInterpolation: true });
            let parts = text.split(/({\w+?}|{\/\w+?})/g); // support `text {0}wrapped text{/0} text`
            if (!isMounted.current) {
                return;
            }
            setText(text);
            setParts(parts);
        });
    }, [children, content, i18n, ns, t]);

    const parseText = () => {
        let reducedParts = parts;
        let result = reducedParts.reduce((acc, part, i) => {
            if (part.startsWith('{/') || (parts[i + 1] && parts[i + 1].startsWith('{/'))) {
                return acc;
            }

            if (!part.startsWith('{')) {
                // not a parameter, return text
                acc.push(part);
                return acc;
            }

            let prop = part.substring(1, part.length - 1);
            if (typeof params[prop] === 'undefined') {
                acc.push(part);
                return acc;
            }

            if (typeof params[prop] === 'object') {
                // React component
                // since we render array of components, React wants a unique key for each.
                let key = `${prop}_${Math.random()}`;
                if (parts[i + 2] && parts[i + 2].startsWith('{/')) {
                    let clone = React.cloneElement(params[prop], {
                        key: key,
                        children: [params[prop].props.children, parts[i + 1]]
                    });
                    acc.push(clone);
                } else {
                    acc.push(<React.Fragment key={key}>{params[prop]}</React.Fragment>);
                }
                return acc;
            }

            acc.push(params[prop]);
            return acc;
        }, []);
        return result;
    };

    const renderWithContent = () => {
        if (!text) {
            return '';
        }

        let result;
        if (typeof params === 'undefined') {
            result = text;
        } else {
            result = parseText();
        }

        if (tag) {
            if (result instanceof Array) {
                result = result.join('');
            }
            if (prepend) {
                result = prepend + result;
            }
            return React.createElement(tag, {
                ...attr,
                className: className,
                dangerouslySetInnerHTML: { __html: result }
            });
        }

        if (prepend) {
            result = prepend + result;
        }

        return result;
    };

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (typeof children === 'function') {
            i18n.loadNamespaces(ns, (err) => {
                if (err) {
                    console.log(err[0]);
                }
                setLoaded(true);
            });
        } else {
            loadText();
        }
    }, [i18n, ns, children, loadText]);

    return typeof children === 'function' ? children(translate) : renderWithContent();
};

Translate.propTypes = {
    /** Namespace | Property file name from translation project */
    ns: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
    /** Property key from translation project */
    content: PropTypes.string,
    /** Property key from translation project */
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /** Parameters in the text */
    params: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    /** Create HTML tag */
    tag: PropTypes.string,
    /** Provide className for the tag */
    className: PropTypes.string,
    /** DOM attributes */
    attr: PropTypes.object,
    /** prepend string to the translated text*/
    prepend: PropTypes.string
};

export default withTranslation()(Translate);
